// Parses an SSE stream from /api/concierge and dispatches text deltas
// and completed tool calls to the supplied handlers. Tools that the
// client doesn't handle (e.g. server-side web_search) are ignored.

export interface ConciergeMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ConciergeRequest {
  messages: ConciergeMessage[];
  trip: unknown;
  // Durable user preferences (dietary, mobility, taste). Prepended to the
  // server-side system prompt when present. Empty string or undefined =
  // no preferences set.
  preferences?: string;
  // Client local calendar day (YYYY-MM-DD) and UTC offset in minutes
  // (Date.getTimezoneOffset()). Let the daily message budget reset at the
  // user's LOCAL midnight rather than midnight UTC.
  localDate?: string;
  tzOffsetMinutes?: number;
  // Supabase access token (session.access_token) when signed in. Sent as a
  // Bearer header so the server can key the daily budget per-USER instead of
  // per-IP. Omitted when anonymous.
  accessToken?: string;
  // Composer preset id, sent with a trip's FIRST message only. The server
  // validates it against its own dataset (api/_preset-picks.js) and
  // injects that preset's verified starter picks into the system prompt.
  // Picks deliberately never travel in chat text: user-controlled text
  // must not be able to smuggle "pre-verified" places past the trust rail.
  presetId?: string;
}

// Structured error so callers can branch on cause (e.g. show friendly
// "concierge at capacity" copy on rate limits instead of a raw stack).
export class ConciergeError extends Error {
  code?: string;
  // For code === "daily_limit": ISO timestamp when the budget resets, so
  // the UI can show "resets in ~Xh".
  resetAt?: string;
  constructor(message: string, code?: string, resetAt?: string) {
    super(message);
    this.name = "ConciergeError";
    this.code = code;
    this.resetAt = resetAt;
  }
}

export interface ConciergeStreamHandlers {
  onText: (textDelta: string) => void;
  onToolCall: (toolName: string, input: unknown) => void;
  onError: (error: ConciergeError) => void;
  onDone: () => void;
}

// Names of tools the client is responsible for applying to local state.
// Anything not in this set (e.g. web_search) is handled server-side by
// Anthropic and ignored here.
const CLIENT_TOOLS = new Set([
  "update_trip_metadata",
  "add_day",
  "remove_day",
  "add_item",
  "update_item",
  "remove_item",
  "add_day_note",
  "set_essentials",
  "present_options",
]);

export async function streamConcierge(
  request: ConciergeRequest,
  handlers: ConciergeStreamHandlers
): Promise<void> {
  let response: Response;
  try {
    // accessToken travels in the Authorization header, not the JSON body.
    const { accessToken, ...body } = request;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    response = await fetch("/api/concierge", {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  } catch (err) {
    handlers.onError(
      new ConciergeError(
        err instanceof Error ? err.message : "Network error reaching the concierge",
        "network"
      )
    );
    return;
  }

  if (!response.ok || !response.body) {
    let message = `HTTP ${response.status}`;
    let code: string | undefined =
      response.status === 429 ? "rate_limit" : "service_error";
    let resetAt: string | undefined;
    try {
      const errBody = await response.text();
      if (errBody) {
        // Try to parse a JSON error body so we can pick up a structured code.
        try {
          const parsed = JSON.parse(errBody);
          // The daily-budget 429 returns a flat shape:
          // { error, code: "daily_limit", resetAt }. Other errors may nest
          // under error.{code,message}. Handle both.
          if (parsed?.code) code = parsed.code;
          if (parsed?.error?.code) code = parsed.error.code;
          if (typeof parsed?.error === "string") message = parsed.error;
          if (parsed?.error?.message) message = parsed.error.message;
          if (parsed?.resetAt) resetAt = parsed.resetAt;
        } catch {
          message = `${message}: ${errBody}`;
        }
      }
    } catch {
      /* ignore */
    }
    handlers.onError(new ConciergeError(message, code, resetAt));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  type Block = { type: string; name?: string; jsonBuffer: string };
  const blocks: Record<number, Block> = {};

  // Whether the stream reached a terminal event (message_stop or error).
  // Mobile radios and backgrounding can close the socket CLEANLY mid-reply;
  // without tracking this, such a truncation would fire neither onDone nor
  // onError and the chat would sit in "streaming" forever.
  let sawTerminal = false;

  const dispatch = (data: any) => {
    const eventType = data.type;
    if (eventType === "content_block_start") {
      const cb = data.content_block ?? {};
      blocks[data.index] = {
        type: cb.type,
        name: cb.name,
        jsonBuffer: "",
      };
    } else if (eventType === "content_block_delta") {
      const block = blocks[data.index];
      if (!block) return;
      const delta = data.delta;
      if (delta?.type === "text_delta" && typeof delta.text === "string") {
        handlers.onText(delta.text);
      } else if (
        delta?.type === "input_json_delta" &&
        typeof delta.partial_json === "string"
      ) {
        block.jsonBuffer += delta.partial_json;
      }
    } else if (eventType === "content_block_stop") {
      const block = blocks[data.index];
      if (block?.type === "tool_use" && block.name && block.jsonBuffer) {
        if (CLIENT_TOOLS.has(block.name)) {
          try {
            const input = JSON.parse(block.jsonBuffer);
            handlers.onToolCall(block.name, input);
          } catch (err) {
            // Malformed tool input JSON, or a throw inside the dispatcher.
            // Skip rather than crash, but never silently: a swallowed tool
            // call here is a dropped itinerary change that the chat will
            // still narrate as done.
            console.warn(`concierge: dropped ${block.name} tool call:`, err);
          }
        }
        // Server-side tools (e.g. web_search) are handled by Anthropic;
        // we ignore their input here.
      }
      delete blocks[data.index];
    } else if (eventType === "message_stop") {
      sawTerminal = true;
      handlers.onDone();
    } else if (eventType === "error") {
      sawTerminal = true;
      handlers.onError(
        new ConciergeError(
          data.error?.message || "Stream error from the concierge",
          data.error?.code
        )
      );
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const eventStr = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        parseSSEEvent(eventStr, dispatch);
      }
    }
    // Clean close without message_stop: the server always ends a healthy
    // stream with a final message_stop, so this is a truncation (typically
    // the OS killing the socket on screen lock or app switch). Surface it
    // as an error so the UI exits the streaming state and offers Retry
    // instead of hanging.
    if (!sawTerminal) {
      handlers.onError(
        new ConciergeError(
          "The connection closed before the reply finished.",
          "stream"
        )
      );
    }
  } catch (err) {
    handlers.onError(
      new ConciergeError(
        err instanceof Error ? err.message : String(err),
        "stream"
      )
    );
  }
}

function parseSSEEvent(
  eventStr: string,
  dispatch: (data: any) => void
): void {
  const lines = eventStr.split("\n");
  let dataStr = "";
  for (const line of lines) {
    if (line.startsWith("data: ")) {
      dataStr += line.slice(6);
    } else if (line.startsWith("data:")) {
      dataStr += line.slice(5);
    }
  }
  if (!dataStr) return;
  try {
    dispatch(JSON.parse(dataStr));
  } catch {
    // Skip malformed payloads.
  }
}
